import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Mail, Send, Save, Trash2, Globe, Building } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = {
  host: '',
  port: 587,
  username: '',
  password: '',
  from_email: '',
  from_name: '',
  reply_to: '',
  security_mode: 'starttls',
  ssl_verify: true,
  enabled: true,
};

function SmtpForm({ scope, initial, onSave, onDelete, canDelete }) {
  const [form, setForm] = useState({ ...EMPTY, ...(initial || {}) });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    setForm({ ...EMPTY, ...(initial || {}) });
  }, [initial]);

  const update = React.useCallback((k, v) => setForm((f) => ({ ...f, [k]: v })), []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, port: parseInt(form.port, 10) || 587 };
      // Ne pas envoyer le champ technique password_set au backend
      delete payload.password_set;
      if (!payload.password && form.password_set) {
        delete payload.password; // backend conserve l'ancien mot de passe
      }
      await onSave(payload);
      toast.success('Configuration enregistrée');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      toast.error('Renseignez un email destinataire pour le test');
      return;
    }
    if (!form.host || !form.from_email) {
      toast.error('Renseignez au minimum le serveur SMTP et l\'email expéditeur');
      return;
    }
    setTesting(true);
    try {
      const payload = {
        ...form,
        port: parseInt(form.port, 10) || 587,
        to_email: testEmail,
        // Si l'utilisateur n'a pas resaisi le mot de passe (champ vide + password_set=true),
        // on envoie une chaîne vide : le backend ira chercher le mot de passe dans la base.
        password: form.password || '',
      };
      // Supprimer password_set qui n'est pas attendu par le backend
      delete payload.password_set;
      const res = await api.post('/smtp-settings/test', payload);
      toast.success(`✅ Email de test envoyé à ${testEmail}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Échec du test';
      toast.error(`Échec SMTP : ${detail}`);
    } finally {
      setTesting(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="space-y-5" data-testid={`smtp-form-${scope}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Hôte SMTP *</Label>
          <Input
            data-testid={`smtp-host-${scope}`}
            required
            placeholder="smtp.gmail.com"
            value={form.host || ''}
            onChange={(e) => update('host', e.target.value)}
          />
        </div>
        <div>
          <Label>Port *</Label>
          <Input
            data-testid={`smtp-port-${scope}`}
            required
            type="number"
            value={form.port || ''}
            onChange={(e) => update('port', e.target.value)}
          />
        </div>
        <div>
          <Label>Utilisateur</Label>
          <Input
            data-testid={`smtp-username-${scope}`}
            value={form.username || ''}
            onChange={(e) => update('username', e.target.value)}
          />
        </div>
        <div>
          <Label>
            Mot de passe {form.password_set && <span className="text-xs text-slate-500">(laissez vide pour conserver)</span>}
          </Label>
          <Input
            data-testid={`smtp-password-${scope}`}
            type="password"
            placeholder={form.password_set ? '••••••••' : ''}
            value={form.password || ''}
            onChange={(e) => update('password', e.target.value)}
          />
        </div>
        <div>
          <Label>Expéditeur (email) *</Label>
          <Input
            data-testid={`smtp-from-email-${scope}`}
            required
            type="email"
            placeholder="noreply@entreprise.com"
            value={form.from_email || ''}
            onChange={(e) => update('from_email', e.target.value)}
          />
        </div>
        <div>
          <Label>Nom expéditeur</Label>
          <Input
            data-testid={`smtp-from-name-${scope}`}
            placeholder="Service RH"
            value={form.from_name || ''}
            onChange={(e) => update('from_name', e.target.value)}
          />
        </div>
        <div>
          <Label>Reply-To</Label>
          <Input
            data-testid={`smtp-reply-to-${scope}`}
            type="email"
            value={form.reply_to || ''}
            onChange={(e) => update('reply_to', e.target.value)}
          />
        </div>
        <div>
          <Label>Sécurité</Label>
          <Select value={form.security_mode || 'starttls'} onValueChange={(v) => update('security_mode', v)}>
            <SelectTrigger data-testid={`smtp-security-${scope}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="starttls">STARTTLS (recommandé)</SelectItem>
              <SelectItem value="ssl">SSL / TLS implicite</SelectItem>
              <SelectItem value="none">Aucune (déconseillé)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <Switch
          data-testid={`smtp-enabled-${scope}`}
          checked={!!form.enabled}
          onCheckedChange={(v) => update('enabled', v)}
        />
        <Label className="cursor-pointer">Activer cette configuration</Label>
      </div>

      <div className="flex items-center gap-3">
        <Switch
          data-testid={`smtp-ssl-verify-${scope}`}
          checked={form.ssl_verify !== false}
          onCheckedChange={(v) => update('ssl_verify', v)}
        />
        <div>
          <Label className="cursor-pointer">Vérifier le certificat SSL</Label>
          <p className="text-xs text-slate-500">
            Désactiver uniquement si votre serveur SMTP utilise un certificat auto-signé
          </p>
        </div>
      </div>

      <div className="border-t pt-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
          <div className="flex-1">
            <Label>Email destinataire du test</Label>
            <Input
              data-testid={`smtp-test-email-${scope}`}
              type="email"
              placeholder="votreemail@exemple.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={handleTest}
            disabled={testing}
            data-testid={`smtp-test-btn-${scope}`}
          >
            <Send className="w-4 h-4 mr-2" />
            {testing ? 'Envoi…' : "Envoyer un email de test"}
          </Button>
        </div>
      </div>

      <div className="flex justify-between pt-2">
        {canDelete ? (
          <Button
            type="button"
            variant="ghost"
            className="text-red-600 hover:text-red-700"
            onClick={onDelete}
            data-testid={`smtp-delete-${scope}`}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Supprimer l&apos;override
          </Button>
        ) : (
          <span />
        )}
        <Button type="submit" disabled={saving} data-testid={`smtp-save-${scope}`}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </div>
    </form>
  );
}

export default function SmtpSettings() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const isCompanyAdmin = user?.role === 'company_admin';
  const hasAccess = !user || isSuperAdmin || isCompanyAdmin;

  const [globalCfg, setGlobalCfg] = useState(null);
  const [companyCfg, setCompanyCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(isSuperAdmin ? 'global' : 'company');

  const load = useCallback(async () => {
    if (!hasAccess) return;
    setLoading(true);
    try {
      if (isSuperAdmin) {
        const r = await api.get('/smtp-settings/global');
        setGlobalCfg(r.data);
      }
      if (isSuperAdmin || isCompanyAdmin) {
        try {
          const r = await api.get('/smtp-settings/company');
          setCompanyCfg(r.data);
        } catch {
          setCompanyCfg(null);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, isCompanyAdmin, hasAccess]);

  useEffect(() => {
    load();
  }, [load]);

  if (!hasAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  const saveGlobal = async (payload) => {
    await api.put('/smtp-settings/global', payload);
    load();
  };
  const saveCompany = async (payload) => {
    await api.put('/smtp-settings/company', payload);
    load();
  };
  const deleteCompany = async () => {
    if (!window.confirm('Supprimer l\'override SMTP de cette entreprise ? Le SMTP global sera utilisé.')) return;
    await api.delete('/smtp-settings/company');
    toast.success('Override supprimé');
    load();
  };

  if (loading) {
    return <div className="p-6 text-slate-500">Chargement…</div>;
  }

  return (
    <div className="space-y-6" data-testid="smtp-settings-page">
      <div className="flex items-center gap-3">
        <Mail className="w-7 h-7 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Paramètres Email (SMTP)</h1>
          <p className="text-sm text-slate-500">
            Configurez l&apos;envoi automatique des notifications de justification.
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              {isSuperAdmin && (
                <TabsTrigger value="global" data-testid="smtp-tab-global">
                  <Globe className="w-4 h-4 mr-2" /> Global (défaut)
                </TabsTrigger>
              )}
              <TabsTrigger value="company" data-testid="smtp-tab-company">
                <Building className="w-4 h-4 mr-2" /> Entreprise
              </TabsTrigger>
            </TabsList>

            {isSuperAdmin && (
              <TabsContent value="global" className="pt-4">
                <p className="text-xs text-slate-500 mb-4">
                  Configuration utilisée par défaut pour toutes les entreprises n&apos;ayant pas leur propre serveur SMTP.
                </p>
                <SmtpForm
                  scope="global"
                  initial={globalCfg}
                  onSave={saveGlobal}
                  canDelete={false}
                />
              </TabsContent>
            )}

            <TabsContent value="company" className="pt-4">
              <p className="text-xs text-slate-500 mb-4">
                Cette configuration prend la priorité sur le SMTP global pour votre entreprise.
              </p>
              <SmtpForm
                scope="company"
                initial={companyCfg}
                onSave={saveCompany}
                onDelete={deleteCompany}
                canDelete={!!(companyCfg && companyCfg.host)}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
